import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiThermometer,
  FiDroplet,
  FiWind,
  FiZap,
  FiWifi,
  FiWifiOff,
  FiPlus,
  FiMapPin,
  FiCpu,
  FiActivity,
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiLink2,
  FiSearch,
} from "react-icons/fi";
import InstallPWAButton from "../components/InstallPWAButton";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import { getMCUsByClientId } from "../api/mcuApi";
import { getMyFarms } from "../api/farmApi";
import SensorTile from "../components/SensorTile";
import { toast } from "react-toastify";
import AcController from "../components/AcController";

const getStoredReadings = (mcu) =>
  typeof mcu.readings === "string" ? JSON.parse(mcu.readings) : mcu.readings || {};

const parseLastSeen = (timestamp) => {
  if (!timestamp) return null;

  const localizedTimestamp = String(timestamp).match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4}),?\s+(\d{1,2}):(\d{2}):(\d{2})$/
  );
  const parsedTime = localizedTimestamp
    ? new Date(
        Number(localizedTimestamp[3]),
        Number(localizedTimestamp[2]) - 1,
        Number(localizedTimestamp[1]),
        Number(localizedTimestamp[4]),
        Number(localizedTimestamp[5]),
        Number(localizedTimestamp[6])
      ).getTime()
    : new Date(timestamp).getTime();

  return Number.isFinite(parsedTime) ? parsedTime : null;
};

const Dashboard = () => {
  const { clientData } = useAuth();
  const { sendCommand, realtimeData } = useSocket();

  const [farms, setFarms] = useState([]);
  const [mcudata, setMcudata] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [lastDataReceived, setLastDataReceived] = useState({});
  const [mcuSearch, setMcuSearch] = useState("");
  const [mcuStatusFilter, setMcuStatusFilter] = useState("all");

  const isMcuOnline = (mcu) => {
    const storedLastSeen =
      parseLastSeen(mcu.updatedAt) ??
      parseLastSeen(getStoredReadings(mcu).lastUpdated);
    const liveLastSeen = lastDataReceived[mcu.mac];
    const lastSeen =
      liveLastSeen !== undefined &&
      (storedLastSeen === null || liveLastSeen > storedLastSeen)
        ? liveLastSeen
        : storedLastSeen;

    return lastSeen !== null && currentTime - lastSeen <= 5 * 60 * 1000;
  };

  const onlineMcuCount = mcudata.filter((mcu) => isMcuOnline(mcu)).length;
  const visibleMcus = mcudata.filter((mcu) => {
    const query = mcuSearch.trim().toLowerCase();
    const matchesSearch =
      !query ||
      [mcu.name, mcu.mac, mcu.farm?.name]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(query));
    const matchesStatus =
      mcuStatusFilter === "all" ||
      (mcuStatusFilter === "online") === isMcuOnline(mcu);
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 30 * 1000); // every 30 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, []);

  // Add this effect to track when realtime data arrives
  useEffect(() => {
    Object.keys(realtimeData).forEach((mac) => {
      if (realtimeData[mac]) {
        setLastDataReceived((prev) => ({
          ...prev,
          [mac]: Date.now(),
        }));
      }
    });
  }, [realtimeData]);

  useEffect(() => {
    const loadMCUs = async () => {
      if (!clientData?.client_id) return;
      const result = await getMCUsByClientId(clientData.client_id);
      setMcudata(result);
    };

    const loadFarms = async () => {
      try {
        if (!clientData?.client_id) return;
        const result = await getMyFarms(clientData.client_id);
        const formatted = result.map((farm) => ({
          ...farm,
          createdAt: new Date(farm.createdAt).toLocaleDateString(),
          lastUpdated: new Date(farm.updatedAt).toLocaleTimeString(),
        }));
        setFarms(formatted);
      } catch (err) {
        toast.info(err.message);
      }
    };

    loadMCUs();
    loadFarms();
  }, [clientData]);

  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "offline":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "active":
        return <FiCheckCircle className="w-4 h-4" />;
      case "warning":
        return <FiAlertTriangle className="w-4 h-4" />;
      case "offline":
        return <FiWifiOff className="w-4 h-4" />;
      default:
        return <FiActivity className="w-4 h-4" />;
    }
  };

  const StatusIndicator = ({
    status,
    label,
    auto,
    onToggle,
    session = false,
  }) => {
    const renderSwitch = (checked, type, accessibleLabel) => (
      <label className="inline-flex cursor-pointer items-center">
        <span className="sr-only">{accessibleLabel}</span>
        <input
          type="checkbox"
          className="peer sr-only"
          checked={Boolean(checked)}
          onChange={(event) =>
            onToggle?.({ type, onOff: event.target.checked, label })
          }
        />
        <span className="relative h-5 w-9 rounded-full bg-gray-200 transition-colors after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:shadow-sm after:transition-transform peer-checked:bg-emerald-600 peer-checked:after:translate-x-4 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-emerald-600" />
      </label>
    );

    return (
      <div className="flex min-w-0 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${status ? "bg-emerald-500" : "bg-gray-300"}`} />
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-gray-800">{label}</p>
            <p className="text-xs text-gray-500">
              {session ? "Session active" : status ? "Running" : "Stopped"}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <div className="flex items-center gap-1.5" title="Automation">
            <FiActivity className="h-3.5 w-3.5 text-gray-400" />
            {renderSwitch(auto, "auto", `${label} automation`)}
          </div>
          <div className="flex items-center gap-1.5" title="Power">
            <FiZap className="h-3.5 w-3.5 text-gray-400" />
            {renderSwitch(status, "status", `${label} power`)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <InstallPWAButton />

      {/* Header Stats */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiMapPin className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Farms</p>
              <p className="text-2xl font-bold text-gray-900">{farms.length}</p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCpu className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Online MCUs</p>
              <p className="text-2xl font-bold text-gray-900">
                {onlineMcuCount}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <FiAlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Warnings</p>
              <p className="text-2xl font-bold text-gray-900">
                {farms.filter((farm) => farm.status === "warning").length}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-lg">
              <FiWifiOff className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Offline MCUs</p>
              <p className="text-2xl font-bold text-gray-900">
                {mcudata.length - onlineMcuCount}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Farms Overview */}
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-100 p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">Your Farms</h2>
            <Link
              to="/farms"
                className="inline-flex items-center rounded-lg bg-emerald-700 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-800"
            >
              <FiPlus className="w-4 h-4 mr-2" />
              Add Farm
            </Link>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {farms.map((farm) => (
              <div
                key={farm.farm_id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{farm.name}</h3>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      farm.status
                    )}`}
                  >
                    {getStatusIcon(farm.status)}
                    <span className="ml-1 capitalize">{farm.status}</span>
                  </span>
                </div>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <FiMapPin className="w-4 h-4 mr-2" />
                    {farm.location}
                  </div>
                  <div className="flex items-center">
                    <FiCpu className="w-4 h-4 mr-2" />
                    {farm.mcuCount || 0} MCUs
                  </div>
                  <div className="flex items-center">
                    <FiClock className="w-4 h-4 mr-2" />
                    Updated {farm.lastUpdated}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MCU Readings */}
      <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="flex flex-col gap-4 border-b border-gray-100 p-4 sm:p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
              <FiCpu className="h-5 w-5" />
            </span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">MCU Overview</h2>
              <p className="text-sm text-gray-500">
                {onlineMcuCount} of {mcudata.length} devices online
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="relative min-w-0 sm:w-64">
              <FiSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={mcuSearch}
                onChange={(event) => setMcuSearch(event.target.value)}
                placeholder="Search device, farm, or MAC"
                aria-label="Search MCUs"
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-800 outline-none transition focus:border-emerald-600 focus:bg-white focus:ring-2 focus:ring-emerald-100"
              />
            </label>
            <select
              value={mcuStatusFilter}
              onChange={(event) => setMcuStatusFilter(event.target.value)}
              aria-label="Filter MCUs by connection status"
              className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 outline-none focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
            >
              <option value="all">All devices</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
            </select>
            <Link
              to="/mcu"
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              Manage MCUs
            </Link>
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {visibleMcus.length > 0 ? (
          <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
            {visibleMcus.map((mcu) => {
              const liveReadings = realtimeData[mcu.mac] || {}; //this is from the websocket hook
              // const liveStatus = deviceStatusUpdate[mcu.mac] || {}; // This also if device send once device status update when the device boot, and rarely to send offline if it sudden turn off, so we cannot rely fully on it
              const readings = {
                ...getStoredReadings(mcu),
                ...liveReadings,
              };

              // const now = new Date(currentTime);
              // const lastUpdated = readings.lastUpdated
              //   ? new Date(readings.lastUpdated)
              //   : null;
              // const minutesSinceUpdate = lastUpdated
              //   ? (now - lastUpdated) / 60000
              //   : Infinity;

              // // If realtime data just came in, we consider it online
              // const hasRealtimeData = !!realtimeData[mcu.mac];

              // With this simple logic:
              const isOnline = isMcuOnline(mcu);

              const sensorCounts =
                mcu.sensors?.reduce((acc, sensor) => {
                  acc[sensor.type] = (acc[sensor.type] || 0) + 1;
                  return acc;
                }, {}) || {};

              return (
                <article
                  key={mcu.mcu_id}
                  className="overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50/70 px-4 py-4 sm:px-5">
                    <div className="min-w-0">
                      <h3 className="truncate font-semibold text-gray-900">
                        {mcu.name}
                      </h3>
                      <p className="mt-0.5 truncate text-sm text-gray-500">
                        {mcu.farm?.name || "Unassigned farm"}
                      </p>
                    </div>
                    <div className={`flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${isOnline ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600"}`}>
                      {isOnline ? (
                        <FiWifi className="h-3.5 w-3.5" />
                      ) : (
                        <FiWifiOff className="h-3.5 w-3.5" />
                      )}
                      <span>{isOnline ? "Online" : "Offline"}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 px-4 pt-3 text-xs text-gray-500 sm:px-5">
                    <FiLink2 className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                    <span className="truncate">
                      {mcu?.profile_links?.[0]?.profile?.name || "No settings profile linked"}
                    </span>
                    {mcu.mac && (
                      <span className="ml-auto shrink-0 font-mono text-gray-400">
                        {mcu.mac}
                      </span>
                    )}
                  </div>

                  {isOnline ? (
                    <div className="p-4 sm:p-5">
                      {/* Sensor Readings */}
                      <div className="mb-5 grid grid-cols-2 gap-x-3 gap-y-4 sm:grid-cols-3 xl:grid-cols-5">
                        <SensorTile
                          label={`Temperature (${readings?.DHT?.count || 0} / ${
                            sensorCounts["DHT"] || 0
                          })`}
                          value={
                            readings?.DHT?.temperature !== undefined
                              ? readings.DHT.temperature.toFixed(1)
                              : "--"
                          }
                          unit="°C"
                          Icon={FiThermometer}
                          bg="bg-red-100"
                          color="text-red-600"
                        />
                        <SensorTile
                          label={`Humidity (${readings?.DHT?.count || 0} / ${
                            sensorCounts["DHT"] || 0
                          })`}
                          value={
                            readings?.DHT?.humidity !== undefined
                              ? readings.DHT.humidity.toFixed(1)
                              : "--"
                          }
                          unit="%"
                          Icon={FiDroplet}
                          bg="bg-blue-100"
                          color="text-blue-600"
                        />
                        <SensorTile
                          label={`Soil Temp (${
                            readings?.SOIL_TEMP?.count || 0
                          } / ${sensorCounts["SOIL_TEMP"] || 0})`}
                          value={
                            readings?.SOIL_TEMP?.soil_temperature !== undefined
                              ? readings.SOIL_TEMP.soil_temperature.toFixed(1)
                              : "--"
                          }
                          unit="°C"
                          Icon={FiThermometer}
                          bg="bg-orange-100"
                          color="text-orange-600"
                        />
                                                 <SensorTile
                            label={`Soil Moist (${
                              readings?.SOIL_MOIST?.count || 0
                            } / ${sensorCounts["SOIL_MOIST"] || 0})`}
                          value={
                            readings?.SOIL_MOIST?.soil_moist !== undefined
                              ? readings.SOIL_MOIST.soil_moist.toFixed(1)
                              : "--"
                          }
                          unit="%"
                          Icon={FiThermometer}
                          bg="bg-orange-100"
                          color="text-orange-600"
                        />
                        <SensorTile
                          label={`CO2 (${readings?.CO2?.count || 0} / ${
                            sensorCounts["CO2"] || 0
                          })`}
                          value={
                            readings?.CO2?.co2 !== undefined
                              ? readings.CO2?.co2.toFixed(1)
                              : "--"
                          }
                          unit="ppm"
                          Icon={FiWind}
                          bg="bg-green-100"
                          color="text-green-600"
                        />
                      </div>

                      <div className="border-t border-gray-100 pt-4">
                        <div className="mb-3 flex items-center justify-between">
                          <p className="text-sm font-semibold text-gray-800">Controls</p>
                          <span className="text-xs text-gray-500">Live device controls</span>
                        </div>
                        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                          <StatusIndicator
                            status={readings.Irrigation?.status}
                            auto={readings.Irrigation?.auto}
                            label="Irrigation"
                            session={readings.Irrigation?.session}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                          {/* <StatusIndicator
                            status={readings?.AC?.status}
                            label="AC"
                            auto={readings?.AC?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                power: onOff,
                                target: label,
                                  "temp": 24,
  "mode": 1,
  "fan": 2
                              });
                            }}
                          /> */}
                          <div className="sm:col-span-2">
                          <AcController
                            temp={readings?.AC?.temp || 24}
                            mode={readings?.AC?.mode || 1}
                            fan={readings?.AC?.fan || 1}  
                            protocol={mcu.protocol || "UNKNOWN"}
                            status={readings?.AC?.status || false}
                            auto={readings?.AC?.auto || false}  
                            count={readings?.AC?.count || 1}
                            onTempChange={(newTemp) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", temp: newTemp,mode: readings?.AC?.mode, fan: readings?.AC?.fan ,power: readings?.AC?.status});
                            }
                            }
                            onModeChange={(newMode) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", mode: newMode,temp: readings?.AC?.temp, fan: readings?.AC?.fan ,power: readings?.AC?.status });
                            } }   
                            onFanChange={(newFan) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", fan: newFan,temp: readings?.AC?.temp,  mode: readings?.AC?.mode ,power: readings?.AC?.status });
                            }
                          }
                            onPowerToggle={(newStatus) => {
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, { type: "status", target: "AC", power: newStatus, temp: readings?.AC?.temp, mode: readings?.AC?.mode, fan: readings?.AC?.fan});
                            } 
                          }
                            
                            onAutoToggle={(newAuto) => {
                              const deviceId = mcu.mac;   
                              sendCommand(deviceId, { type: "auto", target: "AC", onOff: newAuto });
                            }
                            }
                          />
                          </div>


                                                <StatusIndicator
                            status={readings?.Ventilation?.status}
                            label="Ventilation"
                            auto={readings?.Ventilation?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                          <StatusIndicator
                            status={readings?.Humidifier?.status}
                            label="Humidifier"
                            auto={readings?.Humidifier?.auto}
                            onToggle={({ type, onOff, label }) => {
                              // Send Socket.IO command
                              const deviceId = mcu.mac;
                              sendCommand(deviceId, {
                                type,
                                onOff,
                                target: label,
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex min-h-40 flex-col items-center justify-center px-5 py-8 text-center">
                      <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                        <FiWifiOff className="h-5 w-5" />
                      </span>
                      <p className="text-sm font-semibold text-gray-800">Device is offline</p>
                      <p className="mt-1 max-w-xs text-xs text-gray-500">
                        Sensor readings and controls will appear when this MCU reconnects.
                      </p>
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="border-t border-gray-100 px-4 py-3 text-xs text-gray-500 sm:px-5">
                    Last updated: {readings.lastUpdated || "No data received"}
                  </div>
                </article>
              );
            })}
          </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 px-4 py-12 text-center">
              <FiSearch className="mx-auto h-6 w-6 text-gray-400" />
              <p className="mt-3 text-sm font-semibold text-gray-800">No matching MCUs</p>
              <p className="mt-1 text-sm text-gray-500">
                Try another search or change the connection filter.
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
